import React, { useState, useEffect } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, InputAdornment, Paper, Stack, TextField, Typography,
} from '@mui/material';
import {
  AccountBalanceOutlined as AccountIcon,
  AccountBalanceWalletOutlined as BalanceIcon,
  CalendarTodayOutlined as CalendarIcon,
  CheckCircleOutline as CheckIcon,
  EditOutlined as EditIcon,
  Star as StarIcon,
  UpdateOutlined as CurrentIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import * as actions from '../../redux/actions';
import { useBankAccounts } from './hooks/useBankAccounts';
import CompanyLedgerTable from './components/lists/CompanyLedgerTable';
import ExternalPaymentsCard from './components/cards/ExternalPaymentsCard';
import NotFoundPaymentsCard from './components/cards/NotFoundPaymentsCard';
import OcrVerificationHistoryCard from './components/cards/OcrVerificationHistoryCard';
import { formatCurrency } from './utils/format';

const formatThaiDate = (value) => {
  if (!value) return 'ยังไม่กำหนด';
  return new Date(value).toLocaleDateString('th-TH', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
};

function AccountStat({ label, value, helper, icon, tone, action }) {
  const palettes = {
    neutral: { color: '#475569', soft: '#f8fafc', border: '#e2e8f0' },
    income: { color: '#15803d', soft: '#f0fdf4', border: '#bbf7d0' },
    expense: { color: '#dc2626', soft: '#fef2f2', border: '#fecaca' },
    net: { color: '#2563eb', soft: '#eff6ff', border: '#bfdbfe' },
    current: { color: '#2563eb', soft: '#eff6ff', border: '#bfdbfe' },
    verified: { color: '#059669', soft: '#ecfdf5', border: '#a7f3d0' },
  };
  const palette = palettes[tone];
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: palette.border, bgcolor: '#fff', minWidth: 0 }}>
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: palette.soft, color: palette.color, flexShrink: 0 }}>{icon}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Typography sx={{ mt: 0.25, fontSize: { xs: '1.15rem', lg: '1.35rem' }, fontWeight: 800, lineHeight: 1.3, color: palette.color, overflowWrap: 'anywhere' }}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{helper}</Typography>
        </Box>
        {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Stack>
    </Paper>
  );
}

export function CompanyAccount() {
  const dispatch = useDispatch();
  const { bankAccounts, reload: reloadBankAccounts } = useBankAccounts();
  const mainAccount = bankAccounts[0];
  // customer ของหน้านี้: ถ้าตั้งไว้ที่หน้า "ตั้งค่าคู่ยืม-คืน" (company_customer_id) ใช้ตัวนั้น
  // ไม่ได้ตั้ง (ผูกกับฝั่งยืม) = customer_id ของบัญชีแรกเหมือนเดิม
  const loanPair = bankAccounts.find((account) => account.mode === 'BALANCE');
  const customerId = loanPair?.company_customer_id || mainAccount?.customer_id || '';
  const companyOpeningBalance = Number(mainAccount?.company_opening_balance || 0);
  const companyOpeningBalanceDate = mainAccount?.company_opening_balance_date || null;
  const companyOpeningBalanceNote = mainAccount?.company_opening_balance_note || '';
  const companyOpeningBalanceHistory = mainAccount?.company_opening_balance_history || [];

  const [openingBalanceDialogOpen, setOpeningBalanceDialogOpen] = useState(false);
  const [openingBalanceInput, setOpeningBalanceInput] = useState('');
  const [openingBalanceNoteInput, setOpeningBalanceNoteInput] = useState('');
  const [savingOpeningBalance, setSavingOpeningBalance] = useState(false);
  const [openingBalanceError, setOpeningBalanceError] = useState('');

  // กลุ่ม A (สัมพันธ์กับยอดตั้งต้น) — คนละกลุ่มกับยอดรวมรายรับ/รายจ่ายทั้ง database ที่โชว์ใน
  // แถวสรุปท้ายสมุดรายรับ-รายจ่าย (CompanyLedgerTable) ซึ่งไม่เกี่ยวกับยอดตั้งต้นเลย — ก็อปสูตร/
  // แหล่งข้อมูลมาจาก OcrDailyVerification.js เป๊ะ (companyBalanceOcrVerified) เพิ่มแค่ currentBalance
  // ที่ไม่หัก unverified ออก
  const [incomeAfterTotal, setIncomeAfterTotal] = useState(0);
  const [expenseAfterTotal, setExpenseAfterTotal] = useState(0);
  const [incomeUnverifiedTotal, setIncomeUnverifiedTotal] = useState(0);
  const [expenseUnverifiedTotal, setExpenseUnverifiedTotal] = useState(0);

  useEffect(() => {
    if (!customerId || !companyOpeningBalanceDate) return;
    dispatch(actions.listCustomerCashFlowLedger({
      customerId, page: 1, size: 1, dateFrom: companyOpeningBalanceDate,
    })).then((data) => {
      setIncomeAfterTotal(Number(data.incomeAfterTotal || 0));
      setExpenseAfterTotal(Number(data.expenseAfterTotal || 0));
    }).catch(() => {});
  }, [dispatch, customerId, companyOpeningBalanceDate]);

  useEffect(() => {
    if (!customerId || !companyOpeningBalanceDate) return;
    dispatch(actions.listUnverifiedExpenseDates({
      customerId, dateFrom: companyOpeningBalanceDate,
    })).then((data) => {
      const rows = data.rows || [];
      setExpenseUnverifiedTotal(rows.reduce((sum, row) => sum + Number(row.unverifiedExpenseAmount || 0), 0));
      setIncomeUnverifiedTotal(rows.reduce((sum, row) => sum + Number(row.unverifiedIncomeAmount || 0), 0));
    }).catch(() => {});
  }, [dispatch, customerId, companyOpeningBalanceDate]);

  const currentBalance = companyOpeningBalance + incomeAfterTotal - expenseAfterTotal;
  const verifiedIncomeTotal = incomeAfterTotal - incomeUnverifiedTotal;
  const verifiedExpenseTotal = expenseAfterTotal - expenseUnverifiedTotal;
  const companyBalanceOcrVerified = companyOpeningBalance + verifiedIncomeTotal - verifiedExpenseTotal;

  const openOpeningBalanceDialog = () => {
    setOpeningBalanceInput(String(companyOpeningBalance));
    setOpeningBalanceNoteInput('');
    setOpeningBalanceError('');
    setOpeningBalanceDialogOpen(true);
  };

  const saveOpeningBalance = async () => {
    const value = Number(openingBalanceInput);
    if (Number.isNaN(value)) {
      setOpeningBalanceError('กรุณากรอกตัวเลข');
      return;
    }
    if (!openingBalanceNoteInput.trim()) {
      setOpeningBalanceError('กรุณากรอกหมายเหตุการปรับปรุง');
      return;
    }
    setSavingOpeningBalance(true);
    setOpeningBalanceError('');
    try {
      await dispatch(actions.updateBankAccount({
        id: mainAccount.id,
        companyOpeningBalance: value,
        companyOpeningBalanceNote: openingBalanceNoteInput.trim(),
      }));
      setOpeningBalanceDialogOpen(false);
      reloadBankAccounts();
    } catch (err) {
      setOpeningBalanceError(err.message);
    } finally {
      setSavingOpeningBalance(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', pb: 5 }}>
      {!customerId ? (
        <Paper variant="outlined" sx={{ p: 5, borderRadius: 3, borderStyle: 'dashed', textAlign: 'center', bgcolor: '#fafafa' }}>
          <AccountIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 750 }}>กำลังโหลดข้อมูลบัญชีหลัก...</Typography>
          <Typography variant="body2" color="text.secondary">ระบบจะแสดงข้อมูลบัญชีหลัก (ติดดาว) โดยอัตโนมัติ</Typography>
        </Paper>
      ) : (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 750 }}>ภาพรวมบัญชี</Typography>
                <Chip size="small" icon={<StarIcon />} label="บัญชีหลัก" color="warning" variant="outlined" />
              </Stack>
              <Typography variant="body2" color="text.secondary">ยอดสะสมจากรายการทั้งหมดในระบบ</Typography>
            </Box>
          </Stack>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
            <AccountStat
              label="คงเหลือปัจจุบัน"
              value={`${formatCurrency(currentBalance)} บาท`}
              helper="ยอดตั้งต้น + รายรับ − รายจ่าย (นับตั้งแต่กำหนดยอดตั้งต้น)"
              icon={<CurrentIcon />}
              tone="current"
              action={(
                <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={openOpeningBalanceDialog}>
                  ปรับปรุงยอด
                </Button>
              )}
            />
            <AccountStat label="คงเหลือที่ยืนยัน OCR แล้ว" value={`${formatCurrency(companyBalanceOcrVerified)} บาท`} helper="ยอดตั้งต้น + รายรับที่ยืนยันแล้ว − รายจ่ายที่ยืนยันแล้ว" icon={<CheckIcon />} tone="verified" />
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <CompanyLedgerTable customerId={customerId} dateFrom={companyOpeningBalanceDate} openingBalance={companyOpeningBalance} adjustments={companyOpeningBalanceHistory} showStatusColumn />
          </Box>
          <Box sx={{ mt: 2.5 }}><ExternalPaymentsCard /></Box>
          <Box sx={{ mt: 2.5 }}><NotFoundPaymentsCard customerId={customerId} dateFrom={companyOpeningBalanceDate} /></Box>
          <Box sx={{ mt: 2.5 }}><OcrVerificationHistoryCard customerId={customerId} /></Box>
        </>
      )}

      <Dialog open={openingBalanceDialogOpen} onClose={() => setOpeningBalanceDialogOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}><Stack direction="row" spacing={1.25} alignItems="center"><Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#eff6ff', color: '#2563eb' }}><BalanceIcon /></Box><Box><Typography variant="h6" sx={{ fontWeight: 750 }}>ปรับปรุงยอด</Typography><Typography variant="body2" color="text.secondary">ปรับยอดตั้งต้นและเลื่อนจุดเริ่มต้นตรวจสอบบัญชี</Typography></Box></Stack></DialogTitle>
        <DialogContent>
          <Paper variant="outlined" sx={{ p: 2, mt: 1, borderRadius: 2, borderColor: '#e2e8f0', bgcolor: '#f8fafc' }}>
            <Typography variant="body2" color="text.secondary">ยอดตั้งต้นปัจจุบัน</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#475569', lineHeight: 1.3 }}>{formatCurrency(companyOpeningBalance)} บาท</Typography>
            <Typography variant="caption" color="text.secondary">กำหนดเมื่อ {formatThaiDate(companyOpeningBalanceDate)}</Typography>
            {companyOpeningBalanceNote && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                หมายเหตุล่าสุด: {companyOpeningBalanceNote}
              </Typography>
            )}
          </Paper>
          <Alert severity="info" sx={{ my: 1.5, borderRadius: 2 }}>วันที่กดปรับปรุงจะกลายเป็นจุดแบ่งรายการใหม่ รายการก่อนหน้านั้นถือว่ายืนยัน OCR แล้วอัตโนมัติ</Alert>
          <Stack spacing={1.25} sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: '#f8fafc' }}>
            <Stack direction="row" spacing={1} alignItems="center"><CalendarIcon fontSize="small" color="action" /><Typography variant="body2">ไม่เปลี่ยนยอดรายรับ รายจ่าย หรือคงเหลือสุทธิ</Typography></Stack>
            <Stack direction="row" spacing={1} alignItems="center"><BalanceIcon fontSize="small" color="action" /><Typography variant="body2">ใช้คำนวณส่วนต่างที่ยังไม่ได้ยืนยัน OCR เท่านั้น</Typography></Stack>
          </Stack>
          {openingBalanceError && <Alert severity="error" sx={{ mb: 2 }}>{openingBalanceError}</Alert>}
          <TextField autoFocus fullWidth type="number" label="ยอดตั้งต้นใหม่" value={openingBalanceInput} onChange={(event) => setOpeningBalanceInput(event.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">บาท</InputAdornment> }} />
          <TextField
            fullWidth
            required
            multiline
            minRows={2}
            sx={{ mt: 2 }}
            label="หมายเหตุการปรับปรุง"
            placeholder="ระบุเหตุผลที่ปรับปรุงยอด (บังคับกรอกทุกครั้ง)"
            value={openingBalanceNoteInput}
            onChange={(event) => setOpeningBalanceNoteInput(event.target.value)}
            error={Boolean(openingBalanceError) && !openingBalanceNoteInput.trim()}
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}><Button onClick={() => setOpeningBalanceDialogOpen(false)} disabled={savingOpeningBalance}>ยกเลิก</Button><Button variant="contained" onClick={saveOpeningBalance} disabled={savingOpeningBalance || !openingBalanceNoteInput.trim()}>{savingOpeningBalance ? 'กำลังบันทึก...' : 'บันทึกการปรับปรุง'}</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

export default CompanyAccount;
